<template>
    <div class="product-details-canvas">
      <!-- Main Framework Wrapper Container -->
      <div class="container py-3 py-md-5">
        <div class="row">
          
          <!-- 📸 Left Structural Column: Hero Image Media Stage Layer -->
          <section class="col-12 col-md-6 mb-4 mb-md-0">
            <div class="media-gallery-wrapper">
              <!-- Stage Focal Display Panel with Dynamic Graphic Texture Lines Background -->
              <!-- <div class="hero-stage-panel position-relative">
                <button class="back-navigation-btn position-absolute" aria-label="Go back to previous page">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <img :src="activeImage" :alt="product.title" class="img-fluid active-showcase-asset" />
                <span class="slide-indicator-badge position-absolute">
                  {{ activeImageIndex + 1 }}/{{ product.images.length }}
                </span>
              </div> -->
              <div 
                class="hero-stage-panel position-relative" 
                @click="advanceToNextImage"
                style="cursor: pointer;"
                title="Click to view next image"
                >
                    <!-- Back Backwards Navigation Arrow Button Trigger -->
                    <button class="back-navigation-btn position-absolute" @click.stop="triggerBackAction" aria-label="Go back to previous page">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>

                    <!-- Central Active Image View Holder Display Box -->
                    <img :src="activeImage" :alt="product.title" class="img-fluid active-showcase-asset" />

                    <!-- Floating Slide Index Track Indicator Ring Marker Badge -->
                    <span class="slide-indicator-badge position-absolute">
                        {{ activeImageIndex + 1 }}/{{ product.images.length }}
                    </span>
                </div>
              
              <!-- Hidden alternate thumbnails engine array path (Ready for full-size slider loops) -->
              <div class="hidden-thumbnail-preloader-rail d-none">
                <button 
                  v-for="(img, idx) in product.images" 
                  :key="idx" 
                  @click="switchActiveImageIndex(idx)"
                ></button>
              </div>
            </div>
          </section>
  
          <!-- 🏷️ Right Structural Column: Core Metadata Descriptor Block & Specifications -->
          <section class="col-12 col-md-6 d-flex flex-column justify-content-between">
            <div class="product-metadata-block">
              <!-- Product Main Title Header Specification Mapping -->
              <h1 class="product-title-header mb-2">{{ product.title }}</h1>
  
              <!-- Rating Score and Social Review Aggregation Metrics Row Row -->
              <div class="rating-review-metrics-row d-flex align-items-center gap-2 mb-3">
                <div class="score-badge-pill d-flex align-items-center gap-1">
                  <span class="star-icon-solid">★</span>
                  <span class="rating-value-bold">{{ product.ratingScore }}</span>
                </div>
                <span class="review-total-count text-muted">
                  ({{ product.reviewCount }} reviews)
                </span>
                <span class="divider-dot">•</span>
                <span class="availability-status-tag">In Stock</span>
              </div>
  
              <!-- Merchant Verification Authorization Partner Strip Row -->
              <div class="merchant-partnership-row text-muted mb-4">
                Sold by <span class="merchant-highlight-name">{{ product.merchantName }}</span> 
                <span class="partnership-badge-tag">(Official Partner)</span>
              </div>
  
              <!-- 📋 Specifications Grid Layer Table Box Mapping Mockup Properties -->
              <div class="specifications-matrix-table mb-5">
                <h3 class="specification-title-label mb-3">Specifications</h3>
                
                <!-- Property Row 1: Structural Dimensions Parameters -->
                <div class="spec-data-row d-flex justify-content-between py-2 border-bottom">
                  <span class="spec-property-label text-muted">Dimensions</span>
                  <span class="spec-value-content-text text-dark font-weight-bold">
                    {{ product.specifications.dimensions }}
                  </span>
                </div>
  
                <!-- Property Row 2: Texture Composition Material Metrics -->
                <div class="spec-data-row d-flex justify-content-between py-2 border-bottom">
                  <span class="spec-property-label text-muted">Material</span>
                  <span class="spec-value-content-text text-dark font-weight-bold">
                    {{ product.specifications.material }}
                  </span>
                </div>
              </div>

                <div class="product-details-extended">
                    <div class="container py-3 py-md-4">
                    <div class="row">
                        <div class="col-12 mx-auto">
                        
                        <!-- 💳 Payment Plan Section Matrix Grid -->
                        <section class="payment-plan-card mb-4">
                            <h3 class="section-title-label mb-3">Payment Plan</h3>
                            
                            <div class="plan-data-row d-flex justify-content-between align-items-center py-3 border-bottom">
                            <span class="plan-property-label text-muted">Payment Plan</span>
                            <span class="plan-value-highlight text-primary font-weight-bold">
                                ₦{{ formatPrice(paymentInfo.monthlyAmount) }} / Monthly
                            </span>
                            </div>

                            <div class="plan-data-row d-flex justify-content-between align-items-center py-3 border-bottom">
                            <span class="plan-property-label text-muted">Project Duration</span>
                            <span class="plan-value-highlight text-primary font-weight-bold">
                                {{ paymentInfo.durationMonths }} Months
                            </span>
                            </div>
                        </section>

                        <!-- 👤 Merchant Profile Information Block -->
                        <section class="merchant-profile-card d-flex align-items-center gap-3 p-3 mb-5">
                            <div class="merchant-avatar-wrapper position-relative">
                            <img :src="merchant.avatarUrl" :alt="merchant.name" class="merchant-avatar-img" />
                            </div>
                            <div class="merchant-details-meta">
                            <span class="merchant-badge-label uppercase-text text-muted d-block">Merchant</span>
                            <h4 class="merchant-name-title m-0">{{ merchant.name }}</h4>
                            </div>
                        </section>

                        <!-- 💬 Reviews Loop Feed Section Layout -->
                        <section class="customer-reviews-section">
                            <h3 class="section-title-label mb-4">What customers say</h3>
                            
                            <div class="reviews-feed-list">
                            <article 
                                v-for="review in reviews" 
                                :key="review.id" 
                                class="review-item-block py-3 border-bottom"
                            >
                                
                                <header class="review-header-row d-flex justify-content-between align-items-center mb-2">
                                <div class="reviewer-meta d-flex align-items-center gap-2">
                                    <div class="reviewer-initials-avatar d-flex align-items-center justify-content-center">
                                    {{ getInitials(review.authorName) }}
                                    </div>
                                    <h5 class="reviewer-name-text m-0">{{ review.authorName }}</h5>
                                </div>
                                
                                <!-- Stars Row Track Modifier -->
                                <div class="review-stars-row d-flex gap-1">
                                    <span 
                                    v-for="star in 5" 
                                    :key="star" 
                                    class="star-glyph"
                                    :class="{ 'is-filled': star <= review.ratingScore }"
                                    >★</span>
                                </div>
                                </header>

                                <!-- Review Content Paragraph Text Core Block -->
                                <div class="review-body-content">
                                <p class="review-text-paragraph text-muted m-0">
                                    "{{ review.comment }}"
                                </p>
                                </div>
                            </article>
                            </div>
                        </section>

                        </div>
                    </div>
                    </div>
                </div>
            </div>
  
            <!-- Core Operational Purchase Sticky Call-To-Action Foot Panel Row -->
            <footer class="action-docking-row d-flex align-items-center gap-2 pt-3 mt-auto">
              <!-- Quick Buy Instant Activation Checkout Processing CTA Button -->
              <button 
                class="buy-now-action-cta-btn flex-grow-1"
                :disabled="isProcessingBuy"
                @click="dispatchInstantBuyTerminal"
              >
                <span v-if="!isProcessingBuy">Buy Now</span>
                <span v-else>Connecting Terminal Gate...</span>
              </button>
  
              <!-- Bucket Selection Add-To-Cart Icon Trigger Dock Modifier Button Button -->
              <button 
                class="cart-bucket-append-dock-btn d-flex align-items-center justify-content-center"
                :disabled="isProcessingCart"
                @click="dispatchCartAppendPipeline"
                :aria-label="'Append item selection to cart bucket'"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  <line x1="12" y1="9" x2="12" y2="15"></line><line x1="9" y1="12" x2="15" y2="12"></line>
                </svg>
              </button>
            </footer>
          </section>
  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  // @ts-nocheck
  import Vue from 'vue';
  
  export default Vue.extend({
    name: 'ProductDetails',
    data() {
      return {
        activeImageIndex: 0,
        isProcessingBuy: false,
        isProcessingCart: false,
        
        // Master Schema Dataset reflecting the exact details visible in the image mockup
        product: {
          id: "prod-hisense-43a6k",
          title: 'Hisense 43" 4K UHD Smart Frameless TV (43A6K)',
          ratingScore: 4.7,
          reviewCount: 128,
          merchantName: 'Konga Direct',
          images: [
            // Utilizing high-resolution representation path tracking the active product design image
            require('../../assets/images/prop1.svg'),
            require('../../assets/images/prop5.svg'),
          ],
          specifications: {
            dimensions: '112"W x 70"D x 32"H',
            material: 'Italian Linen Blend'
          }
        },
        paymentInfo: {
            monthlyAmount: 2400000,
            durationMonths: 2
        },
        merchant: {
            name: 'Urban Tech Solutions',
            avatarUrl: 'https://unsplash.com'
        },
        reviews: [
            {
            id: 1,
            authorName: 'Julian Steven.',
            ratingScore: 5,
            comment: 'Exceeded expectations. The fabric is incredibly durable and cleans up easily even with kids and pets.'
            },
            {
            id: 2,
            authorName: 'Ayo Mkun.',
            ratingScore: 4,
            comment: 'Exceeded expectations. The fabric is incredibly durable and cleans up easily even with kids and pets.'
            },
            {
            id: 3,
            authorName: 'Emeka Samuel.',
            ratingScore: 3,
            comment: 'Exceeded expectations. The fabric is incredibly durable and cleans up easily even with kids and pets.'
            }
        ]
      };
    },
    computed: {
      activeImage() {
        return this.product.images[this.activeImageIndex] || '';
      }
    },
    methods: {
        switchActiveImageIndex(index) {
            if (index >= 0 && index < this.product.images.length) {
            this.activeImageIndex = index;
            }
        },
        
        // 🎯 THE CAROUSEL STEP FUNCTION
        advanceToNextImage() {
            // If we are on the last image, wrap cleanly back to index 0, otherwise increment
            if (this.activeImageIndex >= this.product.images.length - 1) {
            this.activeImageIndex = 0;
            } else {
            this.activeImageIndex++;
            }
            console.log(`[Carousel Swipe]: Advanced dynamically to active slide view index: ${this.activeImageIndex}`);
        },

        triggerBackAction() {
            console.log('[Navigation]: Back action triggered');
            // Implement your routing logic context path here (e.g., this.\$router.back())
        },

        dispatchInstantBuyTerminal() {
            this.isProcessingBuy = true;
            setTimeout(() => {
            this.isProcessingBuy = false;
            this.$emit('buy-now', { productId: this.product.id, quantity: 1 });
            }, 1000);
        },

        dispatchCartAppendPipeline() {
            this.isProcessingCart = true;
            setTimeout(() => {
            this.isProcessingCart = false;
            this.$emit('add-to-cart', { productId: this.product.id, quantity: 1 });
            }, 800);
        },

        formatPrice(val) {
            return val.toLocaleString('en-NG');
        },
        getInitials(name) {
            if (!name) return 'U';
            const parts = name.trim().split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return name.slice(0, 2).toUpperCase();
        }
    }
  });
  </script>


<style lang="scss" scoped>
// Variables local references binding back cleanly to your global typography rules
$font-sans: 'Public Sans', system-ui, -apple-system, sans-serif;
$color-navy-primary: #0022ba; // Matching the deep cobalt buy button hex color token parameter precisely
$color-bg-panel: #f8fafc;
$color-border-ring: #e2e8f0;
$color-text-dark: #0f172a;
$color-text-muted: #64748b;


.product-details-canvas {
  font-family: $font-sans;
  background-color: #ffffff;
  color: $color-text-dark;
  min-height: 100vh;
  box-sizing: border-box;
}

/* ─── 📸 GALLERIES HERO VIEW BOX STAGE ─── */
.hero-stage-panel {
  position: relative;
  background: radial-gradient(circle at 50% 50%, #e0f2fe 0%, #bae6fd 70%, #7dd3fc 100%); // Waves design overlay blueprint tint mapping
  border-radius: 20px;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.02);

  .active-showcase-asset {
    max-height: 85%;
    max-width: 85%;
    object-fit: contain;
    filter: drop-shadow(0 20px 30px rgba(15, 23, 42, 0.15));
  }
}

.back-navigation-btn {
  top: 16px;
  left: 16px;
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.08);
  }
}

.slide-indicator-badge {
  bottom: 16px;
  right: 16px;
  background-color: rgba(15, 23, 42, 0.5); // Frosted layout indicators strip
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  letter-spacing: 0.05em;
  backdrop-filter: blur(4px);
}

/* ─── 🏷️ PRODUCT INFORMATION CORE AND SPECIFICATIONS ─── */
.product-title-header {
  font-family: $font-sans;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.3;
  color: #0d1b3e; // Deep navy text matching mockup specification header layers
  letter-spacing: -0.01em;
  text-align: left;
}

.score-badge-pill {
  background-color: #eff6ff;
  padding: 4px 8px;
  border-radius: 6px;

  .star-icon-solid {
    color: #2563eb; // High contrast blue rating star matching mockup criteria exactly
    font-size: 0.85rem;
  }

  .rating-value-bold {
    font-size: 0.85rem;
    font-weight: 800;
    color: #2563eb;
  }
}

.review-total-count {
  font-size: 0.85rem;
  font-weight: 600;
}

.divider-dot {
  color: #cbd5e1;
  font-size: 0.8rem;
}

.availability-status-tag {
  font-size: 0.8rem;
  font-weight: 700;
  color: #16a34a;
}

.merchant-partnership-row {
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;

  .merchant-highlight-name {
    color: $color-text-dark;
    font-weight: 700;
  }
  
  .partnership-badge-tag {
    font-size: 0.8rem;
    color: $color-text-muted;
  }
}

/* 📋 SPECIFICATIONS LAYOUT MATRIX GRID */
.specification-title-label {
  font-size: 1.1rem;
  font-weight: 800;
  color: #334155;
  letter-spacing: -0.01em;
  text-align: left;
}

.spec-data-row {
  font-size: 0.95rem;
  align-items: center;

  .spec-property-label {
    font-weight: 500;
  }

  .spec-value-content-text {
    font-size: 0.95rem;
    font-weight: 600;
    text-align: right;
  }
}

.reviewer-meta {
    gap: 5px;
}

/* ─── 💳 ACTION DOCK CONTROLS STICKY BUTTONS ─── */
.buy-now-action-cta-btn {
  background-color: $color-navy-primary;
  color: #ffffff;
  border: none;
  height: 52px; // Optimized hit targets matching touchscreen mobile accessibility dimensions
  border-radius: 26px; // Completely round pill configuration matching the image mockup design
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 34, 186, 0.15);
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background-color: lighten($color-navy-primary, 5%);
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }

  &:disabled {
    background-color: $color-text-muted;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.cart-bucket-append-dock-btn {
  width: 52px;
  height: 52px;
  background-color: #ffffff;
  border: 1px solid $color-border-ring;
  color: $color-navy-primary;
  border-radius: 50%; // Circular auxiliary utility trigger
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f8fafc;
    border-color: $color-text-muted;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* ─── 📱 RESPONSIVE VIEWPORT BREAKPOINTS (Pure CSS Media Queries) ─── */
@media (min-width: 768px) {
  .product-title-header {
    font-size: 2rem;
    line-height: 1.25;
  }

  .hero-stage-panel {
    aspect-ratio: 4 / 4.5; // Elongates view canvas parameters automatically on large presentation screens
    padding: 3rem;
  }
  
  .spec-data-row {
    padding: 14px 0 !important; // Gives specification columns clear room layout spacing on desktop screens
  }
}

.product-details-extended {
  font-family: $font-sans;
  background-color: #ffffff;
  color: $color-text-dark;
  box-sizing: border-box;
}

/* ─── 💳 PAYMENT PLAN MODULE ROWS ─── */
.section-title-label {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
  text-align: left;
}

.plan-data-row {
  font-size: 0.95rem;

  .plan-property-label {
    font-weight: 500;
  }

  .plan-value-highlight {
    font-size: 1.05rem;
    font-weight: 800;
    color: $color-navy-primary !important;
  }
}

/* ─── 👤 MERCHANT CARD WRAPPER ─── */
.merchant-profile-card {
  background-color: #f3f6ff; // Accurate light secondary blue highlight tint from design mockup
  border-radius: 14px;
  box-sizing: border-box;
  gap: 10px;

  .merchant-avatar-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;

    .merchant-avatar-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .merchant-badge-label {
    text-align: left;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .merchant-name-title {
    font-size: 1rem;
    font-weight: 800;
    color: #0f172a;
  }
}

/* ─── 💬 CUSTOMER REVIEWS LIST ─── */
.reviewer-initials-avatar {
  width: 36px;
  height: 36px;
  background-color: #e2e8f0;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 50%;
  letter-spacing: 0.02em;
}

.reviewer-name-text {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
}

.review-stars-row {
  .star-glyph {
    color: #e2e8f0; // Empty baseline grey star tracker glyph
    font-size: 0.85rem;
    transition: color 0.2s ease;

    &.is-filled {
      color: #ffd073; // Vibrant filled brand orange-yellow highlight star indicator token
    }
  }
}

.review-body-content {
  padding-left: 44px; // Mobile optimized indentation to align paragraphs right below the names profile tracks
  
  .review-text-paragraph {
    font-size: 0.925rem;
    line-height: 1.5;
    font-style: italic;
    text-align: left;
  }
}

/* ─── 📱 RESPONSIVE VIEWPORT BREAKPOINTS (Pure CSS Media Queries) ─── */
@media (min-width: 640px) {
  .section-title-label {
    font-size: 1.5rem;
  }

  .plan-data-row {
    padding: 18px 0 !important;
    
    .plan-value-highlight {
      font-size: 1.2rem;
    }
  }

  .merchant-profile-card {
    padding: 1.25rem !important;

    .merchant-avatar-wrapper {
      width: 56px;
      height: 56px;
    }
  }
}
</style>

  